/**
 * ImageRenderer
 * 
 * Handles image rendering for PDF including:
 * - SVG logos (converted to raster via canvas)
 * - PNG logos
 * - Signature images
 * - Watermarks
 */

import { jsPDF } from 'jspdf'
import { Canvg } from 'canvg'
import { CONTROL_CARD_CONFIG, CONTROL_CARD_TEXT } from '../../config'
import { resolveSignatureUrl } from '@/utils/signatureUrl'

/**
 * Image dimensions
 */
export interface ImageDimensions {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Result of image rendering operation
 */
export type ImageRenderResult =
  | { success: true }
  | { success: false; error: string }

/**
 * Renderer for images in PDFs
 */
export class ImageRenderer {
  private readonly config: typeof CONTROL_CARD_CONFIG

  constructor(config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG) {
    this.config = config
  }

  /**
   * Add logo to PDF (auto-detects SVG vs PNG)
   */
  async addLogo(
    doc: jsPDF,
    logoSource: string,
    dimensions: ImageDimensions
  ): Promise<ImageRenderResult> {
    // Check if it's a PNG URL or SVG string
    if (
      logoSource.includes('data:image/png') ||
      logoSource.endsWith('.png') ||
      logoSource.includes('blob:')
    ) {
      return this.addPngImage(doc, logoSource, dimensions)
    } else {
      return this.addSvgImage(doc, logoSource, dimensions)
    }
  }

  /**
   * Add PNG image to PDF
   */
  async addPngImage(
    doc: jsPDF,
    pngSource: string,
    dimensions: ImageDimensions
  ): Promise<ImageRenderResult> {
    try {
      // Ensure coordinates are within PDF bounds
      const safeDimensions = this.ensureSafeBounds(dimensions)

      // Add PNG image directly to PDF
      doc.addImage(
        pngSource,
        'PNG',
        safeDimensions.x,
        safeDimensions.y,
        safeDimensions.width,
        safeDimensions.height
      )

      return { success: true }
    } catch (error) {
      console.error('❌ Error adding PNG image:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  /**
   * Add SVG image to PDF (converts to PNG via canvas)
   */
  async addSvgImage(
    doc: jsPDF,
    svgString: string,
    dimensions: ImageDimensions
  ): Promise<ImageRenderResult> {
    try {
      if (!svgString || svgString.trim().length === 0) {
        throw new Error('Empty SVG string provided')
      }

      // Create canvas for SVG rendering
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        throw new Error('Could not get 2D context from canvas')
      }

      // Set canvas dimensions with high resolution
      const scale = 4 // Higher resolution for better quality
      canvas.width = dimensions.width * scale
      canvas.height = dimensions.height * scale

      // Set white background for better logo visibility
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Try native browser SVG rendering first, fall back to canvg
      try {
        await this.renderSvgNative(svgString, canvas, ctx)
      } catch {
        await this.renderSvgWithCanvg(svgString, canvas, ctx)
      }

      // Convert canvas to PNG and add to PDF
      const imgData = canvas.toDataURL('image/png', 1.0)
      const safeDimensions = this.ensureSafeBounds(dimensions)

      doc.addImage(
        imgData,
        'PNG',
        safeDimensions.x,
        safeDimensions.y,
        safeDimensions.width,
        safeDimensions.height
      )

      return { success: true }
    } catch (error) {
      console.error('❌ Error rendering SVG image:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  /**
   * Render SVG using native browser capabilities
   */
  private async renderSvgNative(
    svgString: string,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const img = new Image()
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(svgBlob)

      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        URL.revokeObjectURL(url)
        resolve()
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Failed to load SVG as image'))
      }

      img.src = url
    })
  }

  /**
   * Render SVG using Canvg library
   */
  private async renderSvgWithCanvg(
    svgString: string,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
  ): Promise<void> {
    const v = Canvg.fromString(ctx, svgString, {
      ignoreDimensions: true,
      scaleWidth: canvas.width,
      scaleHeight: canvas.height,
    })

    await v.render()
  }

  /**
   * Add signature image to PDF within a table cell
   */
  async addSignatureImage(
    doc: jsPDF,
    signatureData: string | undefined,
    cell: {
      x: number
      y: number
      width: number
      height: number
    }
  ): Promise<ImageRenderResult> {
    if (!signatureData) {
      return { success: false, error: 'No signature data provided' }
    }

    try {
      // Normalize signature data to data URL
      const dataUrl = await this.normalizeSignatureData(signatureData)
      if (!dataUrl) {
        return { success: false, error: 'Failed to normalize signature data' }
      }

      // Get image properties
      const props = (doc as any).getImageProperties?.(dataUrl)
      const intrinsicWidth = props?.width || 350
      const intrinsicHeight = props?.height || 120
      const intrinsicRatio = intrinsicWidth / intrinsicHeight

      // Calculate drawing dimensions with padding
      const padding = this.config.signature.padding
      const maxWidth = cell.width - padding * 2
      const maxHeight = cell.height - padding * 2

      // Fit image within cell preserving aspect ratio
      let drawWidth = maxWidth
      let drawHeight = drawWidth / intrinsicRatio

      if (drawHeight > maxHeight) {
        drawHeight = maxHeight
        drawWidth = drawHeight * intrinsicRatio
      }

      // Scale down by configured amount
      const scale = this.config.signature.scale
      drawWidth = drawWidth * scale
      drawHeight = drawHeight * scale

      // Center within the cell
      const x = cell.x + (cell.width - drawWidth) / 2
      const y = cell.y + (cell.height - drawHeight) / 2

      doc.addImage(dataUrl, 'PNG', x, y, drawWidth, drawHeight)

      return { success: true }
    } catch (error) {
      console.error('❌ Error adding signature image:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  /**
   * Normalize signature data to data URL
   */
  private async normalizeSignatureData(
    signatureData: string
  ): Promise<string | null> {
    try {
      // Already a data URL
      if (signatureData.startsWith('data:image/')) {
        return signatureData
      }

      // HTTP URL - fetch and convert
      if (signatureData.startsWith('http')) {
        const response = await fetch(await resolveSignatureUrl(signatureData))
        const blob = await response.blob()
        return await this.blobToDataUrl(blob)
      }

      // Base64 string without header
      if (signatureData.length > 50 && !signatureData.includes(' ')) {
        return `data:image/png;base64,${signatureData}`
      }

      return null
    } catch (error) {
      console.warn('Failed to normalize signature data:', error)
      return null
    }
  }

  /**
   * Convert blob to data URL
   */
  private async blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  /**
   * Add watermark to PDF - single centered, transparent watermark
   */
  addWatermark(doc: jsPDF, text: string = CONTROL_CARD_TEXT.no.watermark): void {
    // Save current state
    const currentFont = doc.getFont()
    const currentFontSize = doc.getFontSize()

    const pageDimensions = this.getPageDimensions(doc)

    // Set up watermark style with transparency
    doc.setFont(
      this.config.typography.watermark.font,
      this.config.typography.watermark.weight
    )
    doc.setTextColor(...this.config.colors.watermark)
    
    // Apply transparency using GState (0.3 = 30% opacity for subtle watermark)
    const gState = new (doc as any).GState({ opacity: 0.25 })
    doc.setGState(gState)

    // Add single centered watermark
    for (const position of this.config.watermark.positions) {
      const x = pageDimensions.width * position.x
      const y = pageDimensions.height * position.y

      doc.setFontSize(this.config.typography.watermark.size)
      doc.text(text, x, y, { align: 'center' })
    }

    // Restore opacity and previous state
    const normalState = new (doc as any).GState({ opacity: 1 })
    doc.setGState(normalState)
    doc.setFont(currentFont.fontName, currentFont.fontStyle)
    doc.setFontSize(currentFontSize)
    doc.setTextColor(...this.config.colors.black)
  }

  /**
   * Ensure image dimensions are within PDF bounds
   */
  private ensureSafeBounds(dimensions: ImageDimensions): ImageDimensions {
    const maxX = this.config.page.width - dimensions.width
    const maxY = this.config.page.height - dimensions.height

    return {
      x: Math.max(0, Math.min(dimensions.x, maxX)),
      y: Math.max(0, Math.min(dimensions.y, maxY)),
      width: dimensions.width,
      height: dimensions.height,
    }
  }

  /**
   * Get page dimensions from jsPDF document
   */
  private getPageDimensions(doc: jsPDF): { width: number; height: number } {
    const pageSize = (doc as any).internal.pageSize
    return {
      width: pageSize.width ? pageSize.width : pageSize.getWidth(),
      height: pageSize.height ? pageSize.height : pageSize.getHeight(),
    }
  }
}
